import { memo, useEffect, useCallback, useRef, useState } from "react";
import Form from "../Form";
import { useSnackbar } from "../../utils/index.js"
import Spinner from "../Spinner.js"
import InformPopup from "../popups/InformPopup.js";
import {
    getMatingFatherData,
    getRelevantMatingSire,
} from "../../api/index.js";
import { Box, Typography, Button } from "@mui/material";
import {
    createLambing,
    getLambings,
    getLambs,
    updateLambing,
    updateLambingSire,
    getUserRole,
} from "../../api/index.js";
import { dayjs } from "../../utils/index.js";

const NO_SIRE_VALUE = "__NO_SIRE__";
const NO_SIRE_OPTION = { value: NO_SIRE_VALUE, text: "Χωρίς πατέρα" };
const matingTypeLabels = {
    Group: "Ομαδική",
    group: "Ομαδική",
    Individual: "Ατομική",
    individual: "Ατομική",
    Artificial: "Τεχνητή",
    artificial: "Τεχνητή",
};

const LambingForm = ({
    datePickerWidth = "595px",
    selectedOwner = {},
    selectedAnimal = {},
    setIsAnimalSelected,
    isCreate,
    lactationNumber,
}) => {
    const [sireDataDropdownValues, setSireDataDropdownValues] = useState([NO_SIRE_OPTION]);
    const [lambingFormStep, setLambingFormStep] = useState(null);
    const [lambsFormContent, setLambsFormContent] = useState([]);
    const [lambingData, setLambingData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formValues, setFormValues] = useState();
    const [lambingsDropdownValues, setLambingsDropdownValues] = useState([]);
    const [lambings, setLambings] = useState([]);
    const [updateLambsFormContent, setUpdateLambsFormContent] = useState([]);
    const [selectedLambing, setSelectedLambing] = useState([]);
    const [userRole, setUserRole] = useState();
    const [sireResolution, setSireResolution] = useState(null);
    const [isMatingWarningPopupOpen, setIsMatingWarningPopupOpen] = useState(false);
    const [matingSubmissionWarnings, setMatingSubmissionWarnings] = useState([]);
    const [pendingLambingSubmission, setPendingLambingSubmission] = useState(null);
    const lambingFormRef = useRef(null);
    const sireLookupRequestRef = useRef(0);
    const allSireOptionsRef = useRef([NO_SIRE_OPTION]);
    const sireLookupTimeoutRef = useRef(null);

    const { success, error } = useSnackbar();

    const closeMatingWarningPopup = () => {
        setIsMatingWarningPopupOpen(false);
        setMatingSubmissionWarnings([]);
        setPendingLambingSubmission(null);
    };

    const submitLambingCreation = async (valuesToSend, confirmMatingWarnings = false) => {
        const result = await createLambing(valuesToSend, confirmMatingWarnings);

        if (result.requiresMatingWarningConfirmation) {
            setPendingLambingSubmission(valuesToSend);
            setMatingSubmissionWarnings(result.warnings || []);
            setIsMatingWarningPopupOpen(true);
            return;
        }

        if (result.success) {
            closeMatingWarningPopup();
            setIsAnimalSelected(false);
            success(result.message);
        } else {
            error(result.message);
        }
    };

    const confirmMatingWarningSubmission = async () => {
        if (!pendingLambingSubmission) return;

        setIsMatingWarningPopupOpen(false);
        setIsLoading(true);
        try {
            await submitLambingCreation(pendingLambingSubmission, true);
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    };

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { sireDataArray } = await getMatingFatherData(selectedAnimal._id);
                const seenSireIds = new Set();
                const sireOptions = [
                    NO_SIRE_OPTION,
                    ...sireDataArray.flatMap((sire) => {
                        if (!sire[3] || seenSireIds.has(sire[3])) return [];
                        seenSireIds.add(sire[3]);
                        return [{
                            value: sire[3],
                            text: `${sire[0]} - ${sire[1]}`,
                        }];
                    }),
                ];
                allSireOptionsRef.current = sireOptions;
                setSireDataDropdownValues(sireOptions);
                const { role } = await getUserRole();
                setUserRole(role);

                if (!isCreate) {
                    const { success: scs, lambings, message } = await getLambings(selectedOwner._id, selectedAnimal._id);
                    if (scs) {
                        setLambings(lambings);
                        setLambingsDropdownValues(lambings.map((lambing) => ({ value: new Date(lambing.lambingDate).toLocaleDateString("el-GR"), text: new Date(lambing.lambingDate).toLocaleDateString("el-GR") })))
                        setLambingFormStep(0.5)
                    } else {
                        setIsAnimalSelected(false);
                        error(message);
                    }
                } else {
                    setLambingFormStep(1)
                }
            } catch (err) {
                error(err);
            }

            setIsLoading(false);
        }
    )
    useEffect(() => {
        fetchData();

        return () => {
            if (sireLookupTimeoutRef.current) {
                clearTimeout(sireLookupTimeoutRef.current);
            }
        };
    }, []);

    const handleLambingDateChange = useCallback(async (value) => {
        if (!isCreate) return;

        if (sireLookupTimeoutRef.current) {
            clearTimeout(sireLookupTimeoutRef.current);
            sireLookupTimeoutRef.current = null;
        }

        const requestId = sireLookupRequestRef.current + 1;
        sireLookupRequestRef.current = requestId;

        if (!value || !dayjs(value).isValid()) {
            setSireResolution(null);
            setSireDataDropdownValues(allSireOptionsRef.current);
            lambingFormRef.current?.setFieldValue("sireCode", NO_SIRE_VALUE);
            return;
        }

        try {
            const result = await getRelevantMatingSire(
                selectedAnimal._id,
                dayjs(value).toISOString(),
            );

            if (requestId !== sireLookupRequestRef.current) return;

            if (!result.success) {
                setSireResolution({ type: "error", message: result.message });
                setSireDataDropdownValues(allSireOptionsRef.current);
                lambingFormRef.current?.setFieldValue("sireCode", NO_SIRE_VALUE);
                return;
            }

            const candidateOptions = result.candidates.map((candidate) => ({
                value: candidate.sireId,
                text: candidate.ownerName
                    ? `${candidate.registrationCode} - ${candidate.ownerName}`
                    : candidate.registrationCode,
            }));
            const mergedSireOptions = [...allSireOptionsRef.current];
            for (const candidateOption of candidateOptions) {
                if (!mergedSireOptions.some((option) => option.value === candidateOption.value)) {
                    mergedSireOptions.push(candidateOption);
                }
            }
            allSireOptionsRef.current = mergedSireOptions;
            setSireDataDropdownValues(mergedSireOptions);

            setSireResolution({
                type: result.warnings?.length > 0
                    ? "warning"
                    : (result.matingCandidates?.length > 1 ? "ambiguous" : result.resolution),
                message: result.message,
                warnings: result.warnings || [],
                candidates: result.candidates,
                matingCandidates: result.matingCandidates,
                matingDate: result.matingDate,
                conceptionWindowStart: result.conceptionWindowStart,
                conceptionWindowEnd: result.conceptionWindowEnd,
            });

            lambingFormRef.current?.setFieldValue(
                "sireCode",
                result.suggestedSireId || NO_SIRE_VALUE,
            );
        } catch (err) {
            if (requestId === sireLookupRequestRef.current) {
                setSireResolution({
                    type: "error",
                    message: "Δεν ήταν δυνατή η αναζήτηση της σχετικής σύζευξης",
                });
                setSireDataDropdownValues(allSireOptionsRef.current);
                lambingFormRef.current?.setFieldValue("sireCode", NO_SIRE_VALUE);
            }
        }
    }, [isCreate, selectedAnimal._id]);

    const scheduleLambingDateLookup = useCallback((value) => {
        sireLookupRequestRef.current += 1;

        if (sireLookupTimeoutRef.current) {
            clearTimeout(sireLookupTimeoutRef.current);
        }

        sireLookupTimeoutRef.current = setTimeout(() => {
            handleLambingDateChange(value);
        }, 800);
    }, [handleLambingDateChange]);

    const createFormContent = [
        {
            customType: "date-picker",
            id: "lambingDate",
            type: "desktop",
            label: "Ημ/νία Τοκετού",
            width: datePickerWidth,
            background: "third",
            required: true,
            value: formValues?.lambingDate ? dayjs(formValues.lambingDate) : null,
            disabled: (!isCreate) ? true : false,
            onChange: scheduleLambingDateLookup,
            onAccept: handleLambingDateChange,
        },
        {
            customType: "input",
            id: "damCode",
            type: "text",
            value: `${selectedAnimal?.registrationCode} - ${selectedOwner?.lastName} ${selectedOwner?.firstName}`,
            placeholder: "Μητέρα",
            required: true,
            disabled: true,
        },
        {
            customType: "dropdown",
            id: "sireCode",
            items: sireDataDropdownValues,
            placeholder: "Πατέρας",
            defaultValue: formValues?.sireCode || NO_SIRE_VALUE,
        },
        {
            customType: "dropdown",
            id: "owner",
            items: [],
            defaultValue: `${selectedOwner?.code} - ${selectedOwner?.lastName} ${selectedOwner?.firstName}`,
            placeholder: "Εκτροφή",
            required: true,
            disabled: true,
        },
        {
            customType: "input",
            id: "lactationNumber",
            type: "number",
            placeholder: "Αριθμός Γαλακτικής Περιόδου",
            value: lactationNumber || 1,
            disabled: true
        },
        {
            customType: "input",
            id: "femaleAlive",
            type: "number",
            placeholder: "Ζωντανά Θηλυκά",
            required: true,
            value: `${formValues?.femaleAlive}`,
            disabled: isCreate ? false : true
        },
        {
            customType: "input",
            id: "maleAlive",
            type: "number",
            placeholder: "Ζωντανά Αρσενικά",
            required: true,
            value: `${formValues?.maleAlive}`,
            disabled: isCreate ? false : true
        },
        {
            customType: "input",
            id: "femaleDead",
            type: "number",
            placeholder: "Νεκρά Θηλυκά",
            required: true,
            value: `${formValues?.femaleDead}`,
            disabled: isCreate ? false : true
        },
        {
            customType: "input",
            id: "maleDead",
            type: "number",
            placeholder: "Νεκρά Αρσενικά",
            required: true,
            value: `${formValues?.maleDead}`,
            disabled: isCreate ? false : true
        },
        {
            customType: "dropdown",
            id: "abortionCode",
            items: [
                { value: "Κανονικός", text: "Κανονικός" },
                { value: "Αποβολή", text: "Αποβολή" },
            ],
            placeholder: "Κατάσταση Τοκετού",
            required: true,
            defaultValue: isCreate ? "Κανονικός" : formValues?.abortionCode,
            disabled: (userRole !== "ADMIN" && !isCreate) ? true : false
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: !isCreate && userRole !== "ADMIN" ? "Αποθήκευση" : "Επόμενο",
        },
    ]

    const selectLambingForm = [
        {
            customType: "dropdown",
            id: "lambing",
            items: lambingsDropdownValues,
            placeholder: "Τοκετός",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επιλογή",
        },
    ]

    var addLambsFormContent = [];
    const submitStep1 = async (values) => {
        setFormValues(values);
        var lambingValues = { ...values };
        // Owner
        lambingValues.owner = selectedOwner?._id;
        // Dam code
        lambingValues.damCode = selectedAnimal?._id;
        // Sire code
        if (values.sireCode && values.sireCode !== NO_SIRE_VALUE) {
            lambingValues.sireCode = values.sireCode;
        } else {
            lambingValues.sireCode = null;
        }
        // prolificacy
        lambingValues.prolificacy = Number(values.maleAlive) + Number(values.maleDead) + Number(values.femaleAlive) + Number(values.femaleDead);


        setLambingData(lambingValues);

        if (!isCreate && userRole !== "ADMIN") {
            setIsLoading(true);
            try {
                const { success: scs, message } = await updateLambingSire(
                    selectedLambing._id,
                    lambingValues.sireCode,
                );
                if (scs) {
                    setIsAnimalSelected(false);
                    success(message);
                } else {
                    error(message);
                }
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
            return;
        }

        if (isCreate) {
            for (var femalecount = 0; femalecount < values.femaleAlive; femalecount++) {
                addLambsFormContent.push({
                    customType: "input",
                    id: `female-lambSlug-${femalecount + 1}`,
                    type: "text",
                    placeholder: `Κωδικός Θηλυκού Αμνού - ${femalecount + 1} (ενώτιο τύπου 2)`,
                    required: true
                });

                addLambsFormContent.push({
                    customType: "dropdown",
                    id: `female-sex-${femalecount + 1}`,
                    items: [],
                    defaultValue: "Θήλυ",
                    placeholder: "Φύλλο",
                    required: true,
                    disabled: true
                });

                addLambsFormContent.push({
                    customType: "input",
                    id: `female-birth-weight-${femalecount + 1}`,
                    type: "number",
                    placeholder: `Βάρος Γέννησης Θηλυκού Αμνού - ${femalecount + 1}`,
                });

            }
            for (var malecount = 0; malecount < values.maleAlive; malecount++) {
                addLambsFormContent.push({
                    customType: "input",
                    id: `male-lambSlug-${malecount + 1}`,
                    type: "text",
                    placeholder: `Κωδικός Αρσενικού Αμνού - ${malecount + 1} (ενώτιο τύπου 2)`,
                    required: true
                });

                addLambsFormContent.push({
                    customType: "dropdown",
                    id: `male-sex-${malecount + 1}`,
                    items: [],
                    defaultValue: "Άρρεν",
                    placeholder: "Φύλλο",
                    required: true,
                    disabled: true
                });

                addLambsFormContent.push({
                    customType: "input",
                    id: `male-birth-weight-${malecount + 1}`,
                    type: "number",
                    placeholder: `Βάρος Γέννησης Αρσενικού Αμνού - ${malecount + 1}`,
                });

                // addLambsFormContent.push({
                //     customType: "input",
                //     id: `male-weaning-weight-${malecount + 1}`,
                //     type: "number",
                //     placeholder: `Βάρος Απογαλακτισμού Αρσενικού Αμνού - ${malecount + 1}`,
                // });
            }


            addLambsFormContent.push({
                customType: "button",
                id: "submit",
                type: "submit",
                text: "Υποβολή",
            })
            setLambsFormContent(addLambsFormContent);
        }
        setLambingFormStep(2);
    };

    const submitFormsHandler = async (values) => {
        setIsLoading(true);
        var lambDataArray = []
        if (isCreate) {
            for (var malAliveCount = 0; malAliveCount < lambingData.maleAlive; malAliveCount++) {
                lambDataArray.push([
                    values[`male-lambSlug-${malAliveCount + 1}`],
                    values[`male-sex-${malAliveCount + 1}`],
                    values[`male-birth-weight-${malAliveCount + 1}`],
                ])
            }
            for (var femAliveCount = 0; femAliveCount < lambingData.femaleAlive; femAliveCount++) {
                lambDataArray.push([
                    values[`female-lambSlug-${femAliveCount + 1}`],
                    values[`female-sex-${femAliveCount + 1}`],
                    values[`female-birth-weight-${femAliveCount + 1}`],
                ])
            }

            const valuesToSend = {
                lambingData,
                lambDataArray
            }
            await submitLambingCreation(valuesToSend);

        } else {
            for (var count = 0; count < Object.keys(values).length / 3; count++) {
                lambDataArray.push([
                    values[`lambSlug-${count + 1}`],
                    values[`sex-${count + 1}`],
                    values[`status-${count + 1}`],
                    values[`birth-weight-${count + 1}`],
                    values[`weaning-weight-${count + 1}`]
                ])
            }

            const valuesToSend = {
                lambingData,
                lambDataArray
            }
            const { success: scs, message } = await updateLambing(selectedLambing._id, valuesToSend);
            if (scs) {
                setIsAnimalSelected(false);
                success(message);
            } else {
                error(message);
            }
        }

        setIsLoading(false);
    }

    const handleSelectLambing = async (values) => {
        const lambing = lambings.find((lmb) => new Date(lmb.lambingDate).toLocaleDateString("el-GR") === values.lambing);
        setSelectedLambing(lambing);
        lambing.abortionCode === "Normal" ? lambing.abortionCode = "Κανονικός" : lambing.abortionCode = "Αποβολή";
        const { lambs } = await getLambs(lambing._id);
        const lambsUpdateForm = [];

        for (var lambCount = 0; lambCount < lambs.length; lambCount++) {
            lambsUpdateForm.push({
                customType: "input",
                id: `lambSlug-${lambCount + 1}`,
                type: "text",
                placeholder: `Κωδικός Αμνού ${lambCount + 1}`,
                required: true,
                value: lambs[lambCount].lambSlug,
                disabled: true
            });

            lambsUpdateForm.push({
                customType: "dropdown",
                id: `status-${lambCount + 1}`,
                items: [
                    { value: "Παρών", text: "Παρών" },
                    { value: "Εγγραφή στην εκτροφή", text: "Εγγραφή στην εκτροφή" },
                    { value: "Σφαγή", text: "Σφαγή" },
                    { value: "Θάνατος", text: "Θάνατος" },
                    { value: "Πώληση", text: "Πώληση" },
                    { value: "Άγνωστη", text: "Άγνωστη" },
                ],
                defaultValue: lambs[lambCount].status,
                placeholder: "Κατάληξη Αμνού",
                required: true,
                disabled: userRole !== "ADMIN" ? true : false
            });

            lambsUpdateForm.push({
                customType: "dropdown",
                id: `sex-${lambCount + 1}`,
                items: [],
                defaultValue: lambs[lambCount].sex === "male" ? "Άρρεν" : "Θήλυ",
                placeholder: `Φύλλο Αμνού ${lambCount + 1}`,
                required: true,
                disabled: true
            });

            lambsUpdateForm.push({
                customType: "input",
                id: `birth-weight-${lambCount + 1}`,
                type: "number",
                placeholder: `Βάρος Γέννησης Αμνού ${lambCount + 1}`,
                value: lambs[lambCount].birthWeight,
                disabled: userRole !== "ADMIN" ? true : false
            });

            lambsUpdateForm.push({
                customType: "input",
                id: `weaning-weight-${lambCount + 1}`,
                type: "number",
                placeholder: `Βάρος Απογαλακτισμού Αμνού ${lambCount + 1}`,
                value: lambs[lambCount].weaningWeight,
                disabled: userRole !== "ADMIN" ? true : false
            });
        }
        lambsUpdateForm.push({
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
            disabled: userRole !== "ADMIN" ? true : false
        })

        setUpdateLambsFormContent(lambsUpdateForm)
        setFormValues(lambing);
        setLambingFormStep(1);
    }

    return (
        <>
            <Spinner open={isLoading} />
            <InformPopup
                isOpen={isMatingWarningPopupOpen}
                title="Προειδοποίηση ελέγχου συζεύξεων"
                onClose={closeMatingWarningPopup}
                innerText={matingSubmissionWarnings}
                buttonText="Ακύρωση"
                onClick={closeMatingWarningPopup}
                acceptText="Συνέχεια και Υποβολή"
                onAccept={confirmMatingWarningSubmission}
            />
            {lambingFormStep === 0.5 && (
                <>
                    <Typography variant="h5" sx={{ margin: "1rem" }}>
                        Επιλογή Τοκετού
                    </Typography>
                    <Form content={selectLambingForm} onSubmit={handleSelectLambing} />
                </>
            )
            }
            {lambingFormStep === 1 && (
                <>
                    <Typography variant="h5" sx={{ margin: "1rem" }}>
                        {isCreate ? "Προσθήκη Τοκετού" : "Επεξεργασαία Τοκετού"}
                    </Typography>
                    <Form
                        ref={lambingFormRef}
                        content={createFormContent}
                        onSubmit={submitStep1}
                        toResetForm={false}
                    />
                    {isCreate && sireResolution && (
                        <Typography
                            sx={{
                                width: "100%",
                                maxWidth: "600px",
                                margin: "0 auto 0.75rem",
                                color: sireResolution.type === "resolved" ? "success.main" : "warning.main",
                            }}
                        >
                            {sireResolution.message}
                            {sireResolution.warnings?.length > 0 && (
                                <Box component="ul" sx={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
                                    {sireResolution.warnings.map((warning) => (
                                        <li key={warning}>{warning}</li>
                                    ))}
                                </Box>
                            )}
                            {sireResolution.matingCandidates?.length === 1
                                ? ` (${new Date(sireResolution.matingCandidates[0].matingDate1).toLocaleDateString("el-GR")})`
                                : ""}
                            {sireResolution.matingCandidates?.length > 1 && (
                                <Box component="ul" sx={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
                                    {sireResolution.matingCandidates.map((mating) => (
                                        <li key={mating.matingId}>
                                            {matingTypeLabels[mating.type] || mating.type || "Σύζευξη"}
                                            {`: ${new Date(mating.matingDate1).toLocaleDateString("el-GR")}`}
                                            {mating.matingDate2
                                                ? ` - ${new Date(mating.matingDate2).toLocaleDateString("el-GR")}`
                                                : ""}
                                            {` — ${mating.registrationCode}`}
                                            {mating.ownerName ? ` - ${mating.ownerName}` : ""}
                                        </li>
                                    ))}
                                </Box>
                            )}
                        </Typography>
                    )}
                    <Button
                        onClick={() => setIsAnimalSelected(false)}
                        sx={{
                            marginTop: "1rem",
                            padding: "0.5rem 1rem",
                            backgroundColor: "third",
                            color: "secondary.main",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            variant: "h5",
                        }}
                    >
                        Πίσω
                    </Button>
                </>
            )
            }
            {lambingFormStep === 2 && (
                <>
                    {lambingData.maleAlive + lambingData.femaleAlive > 0 ? (
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            {isCreate ? "Προσθήκη Αμνών" : "Επεξεργασία Αμνών"}
                        </Typography>
                    ) : (
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Δεν προέκυψαν Αμνοί από τον τοκετό
                        </Typography>
                    )}
                    <Form content={isCreate ? lambsFormContent : updateLambsFormContent} onSubmit={submitFormsHandler} toResetForm={false} />
                    <Button
                        onClick={() => setLambingFormStep(1)}
                        sx={{
                            marginTop: "1rem",
                            padding: "0.5rem 1rem",
                            backgroundColor: "third",
                            color: "secondary.main",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            variant: "h5",
                        }}
                    >
                        Πίσω
                    </Button>
                </>

            )
            }
        </>
    )
}

export default memo(LambingForm);
